import { Box, Typography } from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";
import { EntityRef, FactSource } from "../../types/productPlane";
import DerivedTag from "./DerivedTag";
import EntityLink from "./EntityLink";
import { detailLabelStyle, detailValueStyle } from "./LabelValueGrid";
import SourceMarks from "./SourceMarks";

// One entity of a group, with what is said of its relation
export interface RelationItem {
  // The entity; its name is a link that carries id and type
  entity: EntityRef;
  // A stated number of units, as it reads; its number is shown in monospace
  quantity?: string | null;
  // More on the relation, in the quieter colour: a role, a context
  note?: string | null;
  // Set when no document states the relation as such; what it was derived
  // from, where the export says so
  derived?: { basis: string | null } | null;
  sources: FactSource[];
}

export interface RelationGroup {
  // How the entities of the group are linked, e.g. "Procured under"
  label: string;
  items: RelationItem[];
}

export interface GroupedRelationListProps {
  groups: RelationGroup[];
  // The groups one beneath the other (1, the default), or side by side in
  // two columns (2)
  columns?: 1 | 2;
}

// What stands beside an entity in the quieter, smaller type: a note, a
// quantity
const quietCaption = { color: "text.secondary", fontSize: "0.75rem" } as const;

// A quantity that starts with its number: the number, then the words that go
// with it ("120 units", "1 (the first vessel)")
const LEADING_NUMBER = /^(\d[\d.,]*)(?:\s+(.+))?$/;

// A stated quantity: its number in monospace followed by its words, where it
// has any; a quantity in words alone reads as it is
const Quantity: React.FC<{ text: string }> = ({ text }) => {
  const match = LEADING_NUMBER.exec(text.trim());
  return (
    <Box component="span" sx={quietCaption}>
      {match ? (
        <>
          <Box component="span" sx={monospaceStyle}>
            {match[1]}
          </Box>
          {match[2] && ` ${match[2]}`}
        </>
      ) : (
        text
      )}
    </Box>
  );
};

/**
 * Entities grouped by how they are linked: a label per group, then one line
 * per entity with a note, its quantity and its source marks.
 *
 * A group without an entity is left out; renders nothing when all are.
 */
const GroupedRelationList: React.FC<GroupedRelationListProps> = ({
  groups,
  columns = 1,
}) => {
  const shown = groups.filter((group) => group.items.length > 0);
  if (shown.length === 0) {
    return null;
  }

  return (
    <Box
      sx={
        columns === 2
          ? {
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 2,
            }
          : { display: "flex", flexDirection: "column", gap: 1.5 }
      }
    >
      {shown.map((group) => (
        <Box key={group.label} role="group" aria-label={group.label}>
          <Typography
            variant="caption"
            sx={{ ...detailLabelStyle, mb: 0.5 }}
          >
            {group.label}
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {group.items.map((item, index) => (
              // One line of text: the name, what is said of the relation,
              // and the source marks after it
              <Typography
                key={`${item.entity.id}-${index}`}
                component="li"
                variant="body2"
                sx={{ ...detailValueStyle, overflowWrap: "anywhere" }}
              >
                <EntityLink
                  id={item.entity.id}
                  type={item.entity.type}
                  name={item.entity.name}
                />
                {item.derived && (
                  <>
                    {" "}
                    <DerivedTag basis={item.derived.basis} />
                  </>
                )}
                {item.note && (
                  <>
                    {" "}
                    <Box component="span" sx={quietCaption}>
                      {item.note}
                    </Box>
                  </>
                )}
                {item.quantity && (
                  <>
                    {item.note ? " · " : " "}
                    <Quantity text={item.quantity} />
                  </>
                )}
                <SourceMarks sources={item.sources} />
              </Typography>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default GroupedRelationList;
